import i18n from "./i18n";
import { MOUNT } from "./mount";
/**
 * The only place that talks to the server.
 *
 * Two things it centralises, both of which are miserable when scattered:
 *
 *  - THE CSRF HEADER. The session is an httpOnly cookie, so writes must carry
 *    the session's CSRF value in a header. Forgetting it on one call produces a
 *    403 that looks like a permissions bug, so no component is trusted to
 *    remember: every mutating call goes through `write()`, which adds it.
 *  - WHAT AN ERROR IS. The server answers failures as `{error: "..."}` with a
 *    status. `fetch` does not throw on 4xx, so a component checking only for a
 *    network error will happily render "undefined" from an error body. `req()`
 *    turns any non-2xx into a thrown ApiError carrying the server's own words,
 *    which are written to be shown to a person.
 */

export class ApiError extends Error {
  status: number;
  /**
   * The rest of the error body.
   *
   * A refusal is not always only words. `POST /projects/{id}/launch` answers
   * 502 with a `launch_id`, because the attempt IS recorded even when the
   * build system says no - and without carrying the body through, the one
   * screen that could say "recorded as #12" has to throw that away and the
   * reader is left wondering whether anything happened at all.
   */
  body: Record<string, unknown>;
  constructor(status: number, message: string, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * The manual-work document, computed on the bench.
 *
 * Every field is optional in practice: a bench whose harness predates the
 * export publishes nothing, and the page has to say so rather than render an
 * empty document as "no manual work".
 */
export type ManualStep = { n: number; text: string; manual: boolean };

export type ManualCase = {
  id: string;
  name: string;
  why: "design" | "gap" | "gated";
  minutes: number;
  priority?: string;
  level?: string;
  spec?: string;
  preconditions?: string;
  expected?: string;
  steps: ManualStep[];
  steps_total: number;
  steps_manual: number;
  /** Only on `gated` cases: the capabilities this bench does not grant. */
  missing_caps?: string[];
  sync?: {
    verdict: string;
    action: string;
    issues: { key: string; url: string; done: boolean }[];
    diffs: { field: string; jira: string; plan: string }[];
    review: { field: string; jira: string; plan: string }[];
  };
};

export type ManualDoc = {
  generated_at?: number;
  /** False means "this bench's capabilities were not known", NOT "none". */
  caps_known?: boolean;
  caps?: string[];
  jira?: { present: boolean; fetched: string; age_days: number | null };
  scopes?: Record<string, { short: string; title: string; why: string }>;
  counts?: Record<string, number>;
  minutes?: Record<string, number>;
  sync_counts?: Record<string, number>;
  cases?: ManualCase[];
  shed?: number;
  findings?: { kind: string; key: string; title: string; url: string;
               detail: string; action: string }[];
};

export interface Principal {
  name: string;
  authenticated: boolean;
  can_write: boolean;
  /** True while the session still holds the temporary password. The server
   *  refuses every write from such a session, so the UI must not pretend
   *  otherwise. */
  must_change_password: boolean;
  /** Null for the two principals that are not people: the bootstrap session
   *  and the open, no-password-configured one. */
  user_id: number | null;
  is_admin: boolean;
  display_name: string;
}

/** The four rungs, weakest first. The ORDER is the comparison - a component
 *  that hard-codes "maintainer or owner" is one that forgets the next role. */
export const ROLES = ["guest", "operator", "maintainer", "owner"] as const;
export type Role = (typeof ROLES)[number];

/** What each rung buys, in the words the pages use to explain a disabled
 *  button. A FUNCTION rather than the table it used to be: a table of
 *  translated strings would be built once at import, in whichever language
 *  loaded first, and never change again. Callers are components that already
 *  subscribe through `useTranslation`, so they re-render and re-ask. */
export function roleMeans(role: Role): string {
  return i18n.t(`roles.means.${role}`);
}

/** The rung's own name, for showing. The VALUE stays the English identifier -
 *  it is what the API stores and compares - so this is only ever for display,
 *  never for a request body. */
export function roleName(role: Role): string {
  return i18n.t(`roles.name.${role}`);
}

/** Whether `role` reaches `need`. Null means no access at all. */
export function atLeast(role: Role | null | undefined, need: Role): boolean {
  return role ? ROLES.indexOf(role) >= ROLES.indexOf(need) : false;
}

export interface User {
  id: number;
  name: string;
  display_name: string;
  is_active: boolean;
  /** Only for an administrator reading the list; absent otherwise. */
  email?: string;
  is_admin?: boolean;
  source?: "local" | "sso";
  must_change_password?: boolean;
  has_password?: boolean;
  created_at?: number;
  last_login_at?: number | null;
  group_count?: number;
  groups?: { id: number; name: string; manager: number }[];
}

export interface Group {
  id: number;
  name: string;
  description: string;
  created_at: number;
  member_count: number;
  grant_count: number;
}

export interface GroupMember {
  user_id: number;
  name: string;
  display_name: string;
  is_active: number;
  manager: number;
  added_at: number;
}

/** One grant: who, on what, as what. `kind` says whether `name` is a person
 *  or a team, which is the only thing a reader needs to tell them apart. */
export interface Member {
  id: number;
  kind: "user" | "group";
  subject_id: number;
  name: string;
  detail: string;
  role: Role;
  scope: "project" | "bench";
  scope_id: number;
  created_by: string;
  created_at: number;
  inactive?: boolean;
}

/** One choice in the run form's "Tests" list: a pytest marker expression and
 *  what to call it. */
export interface TestSelection {
  marker: string;
  label: string;
}

export interface Project {
  id: number;
  name: string;
  description_md: string;
  created_at: number;
  bench_count: number;
  run_count: number;
  /** `internal`: every signed-in person reads it. `private`: only members. */
  visibility?: "internal" | "private";
  /** What THIS reader may do here, computed by the server. Null means no
   *  access - which they will not normally see, because such a project is not
   *  listed at all. */
  role?: Role | null;
}

/** One field a project's pipeline will accept. `kind` is a hint for the input,
 *  not a rule - the server validates the value and its message names the key. */
export interface LaunchVariable {
  key: string;
  label: string;
  example: string;
  required: boolean;
  kind: string;
}

/**
 * Where a project's launches go, minus anything secret.
 *
 * `token_env` is the NAME of an environment variable on the server; the
 * credential itself is never in the database and never comes back from the
 * API, so a deployment that has not set it simply cannot launch. `ready` says
 * whether it has - which is the difference between the two 503s, and between
 * two different people having something to do.
 */
export interface LaunchTarget {
  gitlab?: string;
  project?: string;
  ref?: string;
  token_env?: string;
  ready?: boolean;
  variables?: LaunchVariable[];
}

/**
 * The values a launchable variable may be chosen from.
 *
 * `source` is what the list IS, and it carries the cost the person is choosing
 * between: `images` are already built and run in minutes, `branches` and `tags`
 * have to be built first. One source per variable, so a single field is one
 * kind of choice - a list mixing both would need a contract that says so per
 * value.
 *
 * `values` MAY BE EMPTY and that is not an error: `note` says why - no read
 * credential, or whatever the other service answered. An empty list must never
 * stop somebody launching.
 */
export interface LaunchValue {
  value: string;
  /** Where this ONE value came from, and therefore what choosing it costs:
   *  `images` are already built, `branches` and `tags` have to be built first.
   *  Per value, not per variable - a field may offer both, and "run 7.2.66 or
   *  build my branch" is one question about one field. */
  source: string;
}

/** What one bench image is, for the build picker (bench build options). */
export interface BenchImage {
  /** The product part of the tag, e.g. `pdc-maint-4d23a5a5`. */
  product: string;
  /** The version runs of that product recorded (HIL_BUILD), e.g. "7.2.72". */
  version: string;
  /** Commits of the bench scripts and of the tests layered on the build. */
  rig: string;
  tests: string;
  /** A second name for another image (`prev`, `local`). */
  pointer: boolean;
  /** Tagged `<build>-r<sha>-h<sha>`; false for older tag shapes. */
  layered: boolean;
  /** The first real image the bench lists: what the nightly campaign takes. */
  newest: boolean;
  last_used: { at: number; campaign_id: number | null; run_name: string } | null;
}

export interface BenchCurrentImage {
  tag: string;
  at: number;
  campaign_id: number | null;
  run_name: string;
}

export interface LaunchOptions {
  /** In the order the server sent them: sources in the order the project
   *  declares, values in the order the other service gave. Never re-sorted
   *  here - the order carries the intent, and putting the ready-made ones
   *  first is a decision somebody made in the configuration. */
  values: LaunchValue[];
  /** Empty when every source answered. Otherwise the failures, joined per
   *  source ("images: 403; branches: 404"), or the reason there is no
   *  credential at all. An empty list with an empty note means the source
   *  answered and had nothing. */
  note?: string;
}

export interface Launch {
  id: number;
  project_id: number;
  requested_by: string;
  reason: string;
  target: string;
  ref: string;
  variables: Record<string, string>;
  created_at: number;
  pipeline_id: number | null;
  pipeline_url: string;
  error: string;
}

export interface Lease {
  actor: string;
  purpose: string;
  session_ref: string;
  acquired_at: number;
  expires_at: number;
  heartbeat_at: number;
  /** Until the deadline. A lease can outlive this while it keeps heartbeating. */
  seconds_left: number;
  /** Since the last heartbeat. Past the grace period it becomes collectable. */
  quiet_for: number;
}

export interface LeaseEvent {
  id: number;
  ts: number;
  event: string;
  actor: string;
  purpose: string;
  took_from: string;
}

/**
 * One entry in the merged bench timeline.
 *
 * `kind` says which record it came from and therefore what it can be trusted to
 * carry: a `lease` knows who booked the rig, an `access` knows who touched it,
 * a `run` knows what it found. Merged on the server so there is one order and
 * one definition of "recent" - answering "what happened between 11:00 and
 * 13:00" from three lists is exactly why the local log is unreadable.
 */
export interface AuditEvent {
  kind: "lease" | "access" | "run";
  ts: number;
  event: string;
  actor: string;
  purpose: string;
  tool?: string;
  access?: "read" | "write" | "";
  holder?: string;
  session_ref?: string;
  source_host?: string;
  run_id?: number | null;
  took_from?: string;
  finished_at?: number | null;
}

export interface AuditBody {
  /** The LIVE lease: who may touch the bench right now. Null when free. */
  lease: Lease | null;
  /**
   * The lease row whether or not it is still live.
   *
   * When `lease` is null but this is set, the last holder's lease EXPIRED
   * without a release — the case where the timeline's newest event is somebody
   * acquiring the bench and nothing closes it.
   */
  last_lease: Lease | null;
  /** null means no access has ever been reported - unaudited, not untouched. */
  last_access_at: number | null;
  events: AuditEvent[];
}

/**
 * A saved report definition.
 *
 * The section list and its allowed detail values are SERVED (`catalogue`), not
 * declared here: a second copy in TypeScript drifts, and the symptom is a form
 * offering a section the server then refuses.
 */
export interface ReportSection {
  type: string;
  detail: string;
  /** grouped | flat, where the section offers it - grouped is the server's
   *  default and files cases under the plan's own categories. */
  layout?: string;
  title?: string;
  text?: string;
}

export interface ReportScope {
  benches: number[];
  runs: { last: number } | number[];
  /** A whole campaign: the runs of its stops. Trumps `runs` (piece 6). */
  campaign?: number;
  scenarios: string;
  verdicts: string[];
  from?: number;
  to?: number;
}

export interface ReportSpec {
  v?: number;
  scope: ReportScope;
  sections: ReportSection[];
}

export interface Report {
  id: number;
  name: string;
  description: string;
  /** 1 for one of the five the bench used to build. Offered first, and a
   *  delete is remembered so a restart does not bring it back. */
  builtin?: number;
  spec: ReportSpec;
  created_at: number;
  updated_at: number;
  updated_by: string;
}

export interface SectionKind {
  type: string;
  detail: string[];
  /** present only for sections that can be laid out grouped or flat */
  layout?: string[];
  what: string;
}

/**
 * How every rendered report is dressed: brand words, accent, verdict colours.
 * Instance configuration, not part of a definition - when the brand changes,
 * every saved report changes with it.
 */
export interface ReportStyle {
  brand: string;
  strapline: string;
  accent: string;
  verdicts: Record<string, { fg: string; bg: string }>;
}

export interface QualityGroup {
  id: string;
  name: string;
  grade: string;
  confidence?: string;
  verdict?: string;
  cases: number;
  scen: number;
  auto?: number;
  draft?: number;
  nocap?: number;
  vacuous?: number;
  stale?: boolean;
  reviewed_at?: string;
  reviewed_by?: string;
  gaps?: string[];
  recommendations?: { cost?: string; text: string }[];
  ids?: string[];
  evidence?: { ever_passed: number; never_passed: string[]; unseen: number };
}

export interface ConfigField {
  key: string;
  label: string;
  example: string;
  /** "caps" gets a chip editor; anything else is a text box. */
  kind: string;
}

/** Fallback only. The real list comes from the bench's project.
 *
 *  The middle member is the catalogue key for the description, not the
 *  description: this is a module constant, so a translated string here would
 *  be frozen at import. The key and the example are not translated - one is a
 *  variable name the harness reads, the other an address. */
export const CONFIG_KEYS = [
  ["HIL_SSH", "config.help.HIL_SSH", "maintenance@192.168.1.67"],
  ["HIL_DUT_HOST", "config.help.HIL_DUT_HOST", "192.168.1.67"],
  ["HIL_API_BASE", "config.help.HIL_API_BASE", "http://192.168.1.67:8123"],
  ["HIL_KVM", "config.help.HIL_KVM", "192.168.1.39"],
  ["HIL_MODEM_HOST", "config.help.HIL_MODEM_HOST", "192.168.2.1"],
  ["HIL_SIVMOCK", "config.help.HIL_SIVMOCK", "http://192.168.1.8:8099/sivmock"],
  ["HIL_CAPS", "config.help.HIL_CAPS", "reversible,reboot,rootshell"],
] as const;

/** One execution profile a bench can prepare, as its product lists it: a
 *  recipe for the rig (layout, data mode, data, goldens). `problem` says why
 *  this bench cannot prepare it right now, when it cannot. */
export interface BenchProfile {
  id: string;
  label?: string;
  kind?: string;
  project?: string;
  device?: string;
  layout?: string;
  mode?: string;
  cases?: number;
  problem?: string;
}

/** What a bench tests on its own, and where that schedule has got to. The
 *  nightly time is UTC; pages show it in the reader's own time. */
export interface BenchAutoInfo {
  settings: { on_new_build: boolean; nightly_utc: string | null; changed_by: string; changed_at: number | null };
  state: {
    last_campaign: { id: number; origin: string; build: string; at: number } | null;
    waiting: { origin: string; build: string; why: string; since: number } | null;
    missed: { day: string; why: string } | null;
    last_nightly: string | null;
    known_builds: number;
    /** The newest image that carried an already-tested product build, and so
     *  started no campaign. */
    same_product?: { build: string; product: string; at: number } | null;
  };
}

/** One stop of a campaign: a profile, its latest attempt and what that ran. */
export interface CampaignStop {
  pos: number;
  profile: string;
  label: string;
  /** How many cases the profile said it had when the campaign was planned. */
  planned: number | null;
  attempts: number;
  job_id: number | null;
  state: string;
  detail: string;
  run_id: number | null;
  run_name: string;
  passed: number;
  failed: number;
  xfailed: number;
  skipped: number;
  ran: number;
  /** The run finished and never reported verdicts: its numbers are not results. */
  lost?: boolean;
  /** When lost: how many of its cases had failed a check before it stopped. */
  failed_checks?: number;
  /** The failed cases of this stop that are known problems in force. */
  known?: string[];
  /** What the stop's run tested and how; campaign detail only. */
  about?: RunAbout | null;
  /** Every failed case of this stop, and their names as the run knew them. */
  failed_ids?: string[];
  failed_names?: Record<string, string>;
}

/** A run per profile on one bench, planned together (piece 4). Green means
 *  everything planned ran and nothing failed; a skipped case is not a pass. */
/** Where a run or a campaign sits, for its breadcrumbs. Each part is null
 *  when unknown (a run nobody queued has no campaign). */
/** What a run tested and how: the plan's groups of the cases it ran, and on
 *  DisplayOS the data mode and the layout. Empty strings where not recorded. */
export interface RunAbout {
  groups: { name: string; count: number; failed: number; about: string }[];
  mode: string;
  layout: string;
  fixture: boolean;
}

export interface Trail {
  project: { id: number; name: string } | null;
  build: { key: string; label: string } | null;
  campaign: { id: number; stop: string } | null;
}

export interface Campaign {
  id: number;
  bench_id: number;
  bench_name?: string;
  /** The project the bench belongs to, when the list says so. */
  project_id?: number | null;
  marker: string;
  build_ref: string;
  reason: string;
  requested_by: string;
  created_at: number;
  cancelled_at: number | null;
  /** Started by a person, or by the bench's own schedule. */
  origin?: "manual" | "new_build" | "nightly";
  status: "running" | "green" | "not_green" | "cancelled";
  planned: number | null;
  /** The stop on the bench now, if any. */
  current: number | null;
  stops: CampaignStop[];
  /** The version the campaign's runs recorded (HIL_BUILD), e.g. "7.2.72". */
  build_label?: string;
  /** The product part of `build_ref`, without rig and harness segments. */
  build_product?: string;
  /** Product and build, on the detail response only. */
  trail?: Trail;
  /** How many stops ran without ever reporting their results. */
  lost?: number;
  /** How many of `failed` are known problems in force. */
  known?: number;
  passed: number;
  failed: number;
  xfailed: number;
  skipped: number;
  ran: number;
}

export interface Job {
  /** What was ASKED for. What actually ran is recorded on the run itself,
   *  read back from the product - and the two differing is the signal, not
   *  noise: `pdc` is a moving branch, a commit is what the next run compares
   *  against. */
  build_ref?: string;
  /** The profile the bench prepares the rig with before the suite; absent or
   *  empty when the job named none. */
  execution_profile?: string;
  id: number;
  bench_id: number;
  state: "queued" | "claimed" | "running" | "done" | "failed" | "cancelled";
  marker: string;
  k_filter: string;
  requested_by: string;
  reason: string;
  run_name: string;
  detail: string;
  created_at: number;
  claimed_at: number | null;
  finished_at: number | null;
}

export interface CapHelp {
  name: string;
  /** What the capability CLAIMS about the rig. Served, never a second copy here. */
  what: string;
}

export interface Bench {
  id: number;
  name: string;
  description: string;
  created_at: number;
  last_seen_at: number | null;
  agent_version: string;
  run_count: number;
  active_run: string | null;
  registered: boolean;
  online: boolean;
  /** What the last run actually declared - not the configured value. */
  caps: string[];
  /** Which run said so; null when none has reported yet. */
  caps_from: string | null;
  /** One line per capability saying what it claims. Absent on older responses. */
  caps_help?: CapHelp[];
  project_id: number | null;
  project_name: string | null;
  notes_md: string;
  config_json?: string;
  lease: Lease | null;
  /** What THIS reader may do with this rig - direct grant or inherited from
   *  the project, whichever is stronger. The pages use it to disable what the
   *  server would refuse, rather than offer it and answer 403. */
  role?: Role | null;
  /** Whether an agent has EVER pulsed here, and whether it pulsed recently.
   *  Stamped only by the heartbeat and the dispatch poll - publishing a run
   *  raises `last_seen_at` and not these - which is what lets a rig with no
   *  agent be told from one whose agent died. Absent on older responses. */
  has_agent?: boolean;
  agent_alive?: boolean;
  /** The automatic-testing settings as stored: {on_new_build, nightly_utc}.
   *  A JSON string; empty when never set. */
  auto_json?: string;
  /** What the AGENT can be asked to do — today only `build`, meaning it can
   *  put a named build on the rig before running. NOT the same as `caps`,
   *  which is what the rig turned out to have on its last run: one is a
   *  request the agent will honour, the other is an observation about the
   *  hardware. Only this one decides whether a version may be named. */
  agent_caps?: string[];
  /** The execution profiles this bench can prepare, as its agent last listed
   *  them. Offered only where `agent_caps` includes `profiles`. */
  profiles?: BenchProfile[];
  /** Its project's "Tests" list, carried here so the run panel does not have
   *  to fetch the project separately. A rig in no project gets the defaults. */
  test_selections?: TestSelection[];
  test_selections_default?: boolean;
}

export interface Run {
  id: number;
  bench_id: number;
  bench_name: string;
  name: string;
  /** Why somebody started it: the sentence typed into Start testing, from the
   *  job that became this run. Null for a run nobody queued. */
  reason?: string | null;
  started_at: number;
  finished_at: number | null;
  outcome: string | null;
  env_json: string;
  step_count: number;
  failed_scenarios: number;
  /** Whether the run covered the whole suite or a slice of it.
   *  "unknown" is the honest answer for runs recorded before run.sh began
   *  writing the selection - NOT a synonym for "full". */
  scope: "full" | "partial" | "unknown";
  /** Which campaign this run is a stop of, and which stop. A campaign queues
   *  one job per stop and the job carries the run's name, so the server can say
   *  it without the browser guessing from timestamps. Null for a run somebody
   *  started by hand; absent on older responses. */
  campaign_id?: number | null;
  campaign_pos?: number | null;
  /** The pytest marker, -k expression and named files, for naming the slice. */
  marker: string;
  k: string;
  files: string;
  /** The verdict breakdown, which the server has always sent and the list has
   *  never read: `failed_scenarios` alone is a number with no denominator, so
   *  "5" sat beside "1901 steps" meaning five of nobody-said-what. Absent on a
   *  run with no junit, hence optional. */
  v_passed?: number;
  v_skipped?: number;
  v_xfailed?: number;
  verdict_count?: number;
  /** The build under test, from hil !36. `build` is a LABEL to read and
   *  `build_ref` the thing to compare by - for SNCF the label is
   *  "1.7.13+dis.1.7.104.0", whose first two members move without the image
   *  changing at all. `build_recorded` is explicit so absence cannot be read
   *  as a build called "". */
  build?: string;
  build_ref?: string;
  build_recorded?: boolean;
  variant?: string;
  variant_label?: string;
}

export interface Transitions {
  previous: { id: number; name: string };
  broke: { scenario: string; was: string; now: string }[];
  fixed: { scenario: string; was: string; now: string }[];
  changed: { scenario: string; was: string; now: string }[];
  compared: number;
  only_now: string[];
  only_then: string[];
}

/** A failing case somebody accounted for, until a date. It stays a failure on
 *  its run; campaigns and Now count it apart so a NEW failure stands out. */
export type KnownProblem = {
  id: number;
  project_id: number;
  scenario: string;
  reason: string;
  expires_at: number;
  created_by: string;
  created_at: number;
  closed_at: number | null;
  /** "passed" when the case passed, "removed" when somebody ended it. */
  closed_why: string;
  closed_by: string;
  closed_run_id: number | null;
  state: "open" | "expired" | "closed";
};

/** The failed step of a case, as much of it as the failure row shows. */
export type FailureEvidence = {
  step_no: number | null;
  action: string | null;
  /** Basenames under the run's shots/: the frame, then golden-ref-/golden-diff-. */
  shots: string[];
  golden?: Record<string, unknown>;
  /** The exception, when the step raised instead of failing a check. */
  error?: string;
};

export interface ScenarioSummary {
  scenario: string;
  steps: number;
  /** How many CHECKS failed. Evidence, not a verdict - a draft scenario can
   *  fail a check and still be an xfail rather than a failure. */
  failed_checks: number;
  /** What the SUITE made of it: passed | failed | xfailed | skipped | error.
   *  Empty when no junit.xml has been uploaded for the run yet. */
  verdict: string;
  message: string;
  first_ts: number | null;
  last_ts: number | null;
}

export interface Step {
  run_id: number;
  seq: number;
  end_offset: number;
  scenario: string | null;
  step_no: number | null;
  action: string | null;
  ok: number | null;
  ts: number | null;
  raw_json: string;
}

/** Every request is prefixed, because the app may be mounted under a sub-path -
 *  and under WHICH one is decided by the URL being visited, not by the build:
 *  the same bundle serves /hil/ on the shared host and / on its own subdomain.
 *  See ./mount. */
const BASE = MOUNT;
/** Where one of a project's scenario lists came from: a run on a bench,
 *  with the build it ran and the fingerprint of the product's test data.
 *  Lists from runs with different fingerprints are never merged - a bench
 *  may run any build, and each build carries its own tests. */
export interface ScenarioSource {
  bench_id: number;
  bench_name: string;
  run_id: number;
  run_name: string;
  started_at: number;
  build: string;
  build_ref: string;
  variant: string;
  variant_label: string;
  product_digest: string;
  harness_digest: string;
  /** The run the reader came from (the `run` query parameter). */
  requested: boolean;
}

/** One picture comparison as a run recorded it. */
export interface Comparison {
  scenario: string;
  step: number | null;
  golden: string;
  similarity: number;
  threshold: number;
  pass: boolean;
  /** Share of the screen left out of the score, and in how many areas. */
  masked_fraction?: number | null;
  mask_areas?: number;
}

export interface ScenarioVerdict {
  verdict: string;
  run_id: number;
  run_name: string;
  at: number;
  build: string;
  /** The case's picture comparisons in that run, when it made any. */
  likeness?: Comparison[];
}

export interface ScenarioRow {
  id: string;
  name: string;
  requires: string[];
  /** In order of first use; empty for a run from before the harness recorded them. */
  verbs: string[];
  draft: boolean;
  group: string;
  group_name: string;
  priority: string;
  tags: string[];
  in_plan: boolean;
  has_source: boolean;
}

export interface ScenarioListRow extends ScenarioRow {
  /** Per bench id: what the newest run that reported the case said (`last`),
   *  and the newest verdict meaning it actually ran (`executed`). */
  benches: Record<string, { last: ScenarioVerdict | null; executed: ScenarioVerdict | null }>;
}

export interface ScenarioList {
  product_digest: string;
  sources: ScenarioSource[];
  requested: boolean;
  scenarios: ScenarioListRow[];
  /** What changed against the next older list. */
  against?: { run_id: number; run_name: string; added: string[]; removed: string[] };
}

/** One case as the newest run of its profile judged it. */
export interface ProfileTreeCase {
  id: string;
  verdict: string;
  name: string;
}

/** The newest campaign stop for a profile: which campaign, how it stands, how the stop went. */
export interface ProfileTreeStop {
  id: number;
  status: string;
  build_ref: string;
  created_at: number;
  state: string;
  run_id: number | null;
  run_name: string;
  planned: number | null;
  ran: number;
  passed: number;
  failed: number;
  xfailed: number;
  skipped: number;
}

/** An execution profile a bench offers, with the newest run of it and the
 *  cases that run judged (piece 6). `cases` on the profile is the count the
 *  bench offers; the run's `cases` are what it reported. Labels for the
 *  customer and screen come from the bench when it sends them. */
export interface ProfileTreeEntry {
  id: string;
  label: string;
  kind?: string;
  project?: string;
  device?: string;
  project_label?: string;
  device_label?: string;
  layout?: string;
  mode?: string;
  cases?: number;
  run: {
    id: number;
    name: string;
    started_at: number;
    finished_at: number | null;
    build: string;
    build_ref: string;
    passed: number;
    failed: number;
    xfailed: number;
    skipped: number;
    cases: ProfileTreeCase[];
  } | null;
  campaign: ProfileTreeStop | null;
}

export interface ProfileTreeBench {
  bench_id: number;
  bench_name: string;
  profiles: ProfileTreeEntry[];
}

export interface ProjectScenarios {
  project: { id: number; name: string };
  benches: { id: number; name: string }[];
  lists: ScenarioList[];
  /** General tests, then each customer and screen; absent from a server that predates piece 6. */
  tree?: ProfileTreeBench[];
}

export interface ScenarioVersion {
  product_digest: string;
  sources: ScenarioSource[];
  requested: boolean;
  definition: ScenarioRow & { source: string; plan: Record<string, unknown> };
}

export interface ScenarioDetail {
  project: { id: number; name: string };
  id: string;
  versions: ScenarioVersion[];
  /** Per bench id, newest first. */
  history: Record<string, ScenarioVerdict[]>;
  /** The goldens the newest comparison of this case measured against, as that
   *  run shipped them. Absent on older services and for cases with no picture. */
  expected?: { run_id: number; run_name: string; at: number; shot: string; golden: string }[];
  /** The reviewed group this case belongs to (from Quality), if any. */
  quality?: { bench: string; id: string; name: string; grade: string; confidence: string; verdict: string;
              stale: boolean; gaps: string[] } | null;
  /** The case as a person does it by hand (from Manual), if any. */
  manual?: { bench: string; minutes: number | null; priority: string; why: string; preconditions: string;
             expected: string; steps: { n: number; text: string }[]; missing_caps: string[] } | null;
  /** A known problem in force for this case. */
  known?: KnownProblem | null;
}

const u = (p: string) => `${BASE}${p}`;

/** A URL for something rendered by the browser rather than fetched by us. */
export const assetUrl = u;

let csrf = "";
export const setCsrf = (v: string) => {
  csrf = v;
};
export const getCsrf = () => csrf;

/**
 * The language the SERVER should answer in.
 *
 * Set explicitly rather than left to the browser's own Accept-Language, because
 * the two can disagree: somebody on an en-US laptop who switched this UI to
 * Polish would otherwise read a Polish page whose error messages arrive in
 * English - and error messages are exactly the text that has to be understood.
 *
 * English is kept as a fallback so a server that has no catalogue for the first
 * choice still answers something readable rather than nothing.
 */
function acceptLanguage(): string {
  const lang = i18n.resolvedLanguage || i18n.language || "en";
  return lang === "en" ? "en" : `${lang}, en;q=0.8`;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { ...(init?.headers as Record<string, string>), "Accept-Language": acceptLanguage() },
  });
  const text = await res.text();
  let body: unknown = undefined;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    // A non-JSON body from a proxy or a crash: keep the raw text as the message
    // rather than reporting a parse error, which tells the reader nothing.
    if (!res.ok) throw new ApiError(res.status, text.slice(0, 200) || res.statusText);
  }
  if (!res.ok) {
    const d = body as { error?: string } | undefined;
    throw new ApiError(res.status, d?.error ?? res.statusText,
                       (d as Record<string, unknown>) ?? {});
  }
  return body as T;
}

/**
 * The headers a mutating call needs. ONE definition, because the last time a
 * call built its own it used `X-CSRF` instead of `X-HILC-CSRF` and every report
 * preview came back 403 — a permissions error, for a request that was correctly
 * authenticated. This file's own docstring already said no component is trusted
 * to remember the header; the rule has to cover this file too.
 */
function writeHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "Accept-Language": acceptLanguage(),
    ...(csrf ? { "X-HILC-CSRF": csrf } : {}),
  };
}

function write<T>(path: string, method: string, payload?: unknown): Promise<T> {
  return req<T>(path, {
    method,
    headers: writeHeaders(),
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
}

/**
 * A mutating call whose answer is a DOCUMENT, not JSON.
 *
 * `req` parses the body as JSON, which is right everywhere except the report
 * preview: the server answers rendered markup. Hand-rolling that one fetch is
 * what produced the wrong CSRF header, so it gets a helper instead.
 */
async function writeText(path: string, payload: unknown): Promise<string> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: writeHeaders(),
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      msg = (JSON.parse(text) as { error?: string }).error || text;
    } catch {
      /* a non-JSON error body is shown as it came */
    }
    throw new ApiError(res.status, msg.slice(0, 300));
  }
  return text;
}

export const api = {
  meta: () =>
    req<{
      service: string;
      phase: number;
      dispatch_enabled: boolean;
      ui_password_set: boolean;
      registration_enabled: boolean;
    }>(u("/api/v1/meta")),

  me: () =>
    req<{
      principal: Principal;
      csrf: string;
      password_required: boolean;
      /** Whether sign-in wants a name as well as a password. The form has to
       *  know before anybody types. */
      named_accounts: boolean;
      groups?: { id: number; name: string; manager: number }[];
    }>(u("/api/v1/me")),

  login: (password: string, username?: string) =>
    write<{
      principal: Principal;
      csrf: string;
      must_change_password: boolean;
    }>(u("/api/v1/login"), "POST",
      username ? { username, password } : { password }),

  changePassword: (current_password: string, new_password: string) =>
    write<{ ok: boolean; principal: Principal; csrf: string }>(
      u("/api/v1/password"),
      "POST",
      { current_password, new_password },
    ),

  logout: () => write<{ ok: boolean }>(u("/api/v1/logout"), "POST", {}),

  projects: () => req<{ projects: Project[] }>(u("/api/v1/projects")),

  /** `test_selections` and its flag come BESIDE `project`, not in it: the
   *  row carries only the raw column, a JSON string. Neither is on the project
   *  list either, which is why `Project` does not declare them. */
  project: (id: number) =>
    req<{ project: Project; benches: Bench[]; role: Role | null;
          members: Member[]; launch?: LaunchTarget;
          /** The run form's "Tests" list for every rig in this project. */
          test_selections?: TestSelection[];
          /** True while the project has not set its own list and uses the
           *  service's defaults. Without the flag an editor showing the
           *  defaults as an ordinary list would save them back unchanged and
           *  FREEZE them - so when the defaults move, this project would not
           *  follow, and nobody would know why. */
          test_selections_default?: boolean }>(u(`/api/v1/projects/${id}`)),

  /** Ask the build system to compose and run something. Ends in hardware
   *  moving, several steps later, so it needs `operator` - the rung the lease
   *  and the job queue already draw. */
  launchProject: (id: number, variables: Record<string, string>, reason: string) =>
    write<{ launch_id: number; pipeline_id?: number; pipeline_url?: string;
            error?: string }>(
      u(`/api/v1/projects/${id}/launch`), "POST", { variables, reason },
    ),

  /** Slow and separate on purpose: it reaches another service. The panel has
   *  to render without it, so every caller treats a failure as "no list". */
  launchOptions: (id: number) =>
    req<{ options: Record<string, LaunchOptions> }>(
      u(`/api/v1/projects/${id}/launch/options`),
    ),

  /** What is ALREADY BUILT and waiting for this rig. Same shape as a
   *  project's launch options and rendered by the same component - but a
   *  different question: a project lists what can be BUILT, a bench lists what
   *  no longer has to be. */
  buildOptions: (benchId: number) =>
    req<{ options: Record<string, LaunchOptions>;
          /** Absent on older services. */
          images?: Record<string, BenchImage>; current?: BenchCurrentImage | null }>(
      u(`/api/v1/benches/${benchId}/build/options`),
    ),

  launches: (id: number) =>
    req<{ launches: Launch[] }>(u(`/api/v1/projects/${id}/launches`)),

  /** What a project tests, from its runs: one list per product fingerprint,
   *  each naming the run it came from. `run`: a run the reader came from,
   *  whose list is marked even when a newer one exists. */
  projectScenarios: (id: number, run?: number) =>
    req<ProjectScenarios>(u(`/api/v1/projects/${id}/scenarios${run ? `?run=${run}` : ""}`)),

  /** One case: as written in each list it is in, and its verdict history. */
  projectScenario: (id: number, sid: string, run?: number) =>
    req<ScenarioDetail>(
      u(`/api/v1/projects/${id}/scenarios/${encodeURIComponent(sid)}${run ? `?run=${run}` : ""}`),
    ),

  createProject: (name: string, description_md: string) =>
    write<{ project_id: number }>(u("/api/v1/projects"), "POST", {
      name,
      description_md,
    }),

  updateProject: (
    id: number,
    patch: {
      name?: string;
      description_md?: string;
      visibility?: "internal" | "private";
      /** A list replaces the project's own; `null` goes back to the service's
       *  defaults. Absent leaves it alone - which is what an editor that has
       *  not been changed must send, or it freezes the defaults it displayed. */
      test_selections?: TestSelection[] | null;
    },
  ) => write<{ ok: boolean }>(u(`/api/v1/projects/${id}`), "PATCH", patch),

  deleteProject: (id: number) =>
    write<{ ok: boolean }>(u(`/api/v1/projects/${id}`), "DELETE", {}),

  benches: (projectId?: number) =>
    req<{ benches: Bench[] }>(
      u(`/api/v1/benches${projectId ? `?project_id=${projectId}` : ""}`),
    ),

  manual: (benchId?: number) =>
    req<{
      manual: ManualDoc;
      at: number | null;
      bench: { id: number; name: string } | null;
      benches?: { id: number; name: string }[];
    }>(u(`/api/v1/manual${benchId ? `?bench_id=${benchId}` : ""}`)),

  quality: (benchId?: number) =>
    req<{
      quality: QualityGroup[];
      bench: string | null;
      bench_id?: number;
      received_at: number | null;
      benches: { id: number; name: string; reviewed: boolean }[];
    }>(u(`/api/v1/quality${benchId ? `?bench_id=${benchId}` : ""}`)),

  history: (benchId?: number, scope?: string) =>
    req<{
      history: {
        run_id: number;
        name: string;
        bench_name: string;
        started_at: number;
        passed: number;
        failed: number;
        skipped: number;
        xfailed: number;
        steps: number;
        scope: "full" | "partial" | "unknown";
        marker: string;
        k: string;
        files: string;
      }[];
      /** How many runs of each scope exist, so a filter that empties the
       *  chart does not read as "there are no runs". */
      available: { full: number; partial: number; unknown: number };
      scope: string;
    }>(u(`/api/v1/history?${new URLSearchParams({
      ...(benchId ? { bench_id: String(benchId) } : {}),
      ...(scope ? { scope } : {}),
    })}`)),

  live: () =>
    req<{
      live: {
        /** Null while the run has not opened - a job still in its self-test gate. */
        run_id: number | null;
        name: string;
        bench_name: string;
        starting?: boolean;
        bench_id: number;
        started_at: number;
        step_count: number;
        step: Step | null;
      }[];
    }>(u("/api/v1/live")),

  jobs: (benchId?: number) =>
    req<{ jobs: Job[] }>(
      u(`/api/v1/jobs${benchId ? `?bench_id=${benchId}` : ""}`),
    ),

  /** `build_ref` empty means what every job meant until now: run whatever is
   *  already on the rig. Naming one is refused with 409 by a bench whose agent
   *  cannot install builds, so the form only offers it where it works. */
  queueJob: (
    bench_id: number,
    marker: string,
    k_filter: string,
    reason: string,
    build_ref = "",
    execution_profile = "",
  ) =>
    write<{ job_id: number }>(u("/api/v1/jobs"), "POST", {
      bench_id,
      marker,
      k_filter,
      reason,
      build_ref,
      ...(execution_profile ? { execution_profile } : {}),
    }),

  cancelJob: (id: number) =>
    write<{ ok: boolean; was: string; stopping: boolean }>(
      u(`/api/v1/jobs/${id}/cancel`),
      "POST",
      {},
    ),

  /** Campaigns, newest first. Narrow them to a bench, to a project, or to a
   *  page: the server caps `limit` at 200 and defaults to 20. */
  campaigns: (q: { bench?: number; project?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.bench) p.set("bench_id", String(q.bench));
    if (q.project) p.set("project_id", String(q.project));
    if (q.limit) p.set("limit", String(q.limit));
    const s = p.toString();
    return req<{ campaigns: Campaign[] }>(u(`/api/v1/campaigns${s ? `?${s}` : ""}`));
  },
  campaign: (id: number) => req<Campaign>(u(`/api/v1/campaigns/${id}`)),
  startCampaign: (bench_id: number, reason: string, build_ref = "") =>
    write<{ campaign_id: number }>(u("/api/v1/campaigns"), "POST", { bench_id, reason, build_ref }),
  cancelCampaign: (id: number) =>
    write<{ ok: boolean; cancelled: number }>(u(`/api/v1/campaigns/${id}/cancel`), "POST", {}),
  retryCampaignStop: (id: number, pos: number) =>
    write<{ job_id: number }>(u(`/api/v1/campaigns/${id}/stops/${pos}/retry`), "POST", {}),
  projectBuilds: (projectId: number) =>
    req<{ builds: import("./components/buildState").ProjectBuild[]; unbuilt: Run[];
          /** Absent on older responses. */
          project?: { id: number; name: string }; bench_count?: number }>(u(`/api/v1/projects/${projectId}/builds`)),
  knownProblems: (projectId: number) =>
    req<{ known: KnownProblem[] }>(u(`/api/v1/projects/${projectId}/known-problems`)),
  addKnownProblem: (projectId: number, scenario: string, reason: string, expires_at: number) =>
    write<{ id: number }>(u(`/api/v1/projects/${projectId}/known-problems`), "POST",
                          { scenario, reason, expires_at }),
  removeKnownProblem: (id: number) =>
    write<{ ok: boolean }>(u(`/api/v1/known-problems/${id}`), "DELETE"),
  benchAuto: (id: number) => req<BenchAutoInfo>(u(`/api/v1/benches/${id}/auto`)),
  setBenchAuto: (id: number, on_new_build: boolean, nightly_utc: string | null) =>
    write<{ ok: boolean }>(u(`/api/v1/benches/${id}/auto`), "PUT", { on_new_build, nightly_utc }),

  bench: (id: number) =>
    req<{
      bench: Bench;
      config_schema: ConfigField[];
      runs: Run[];
      lease_log: LeaseEvent[];
      /** Granted on this rig itself. */
      members: Member[];
      /** Granted on its project, and therefore true here as well. */
      inherited: Member[];
    }>(u(`/api/v1/benches/${id}`)),

  reports: () =>
    req<{ reports: Report[]; catalogue: SectionKind[]; verdicts: string[] }>(
      u("/api/v1/reports")),

  reportStyle: () =>
    req<{ style: ReportStyle; overrides: Partial<ReportStyle>; defaults: ReportStyle }>(
      u("/api/v1/reports/style")),

  /** Replaces the override set; an empty object is an explicit reset. */
  saveReportStyle: (style: Partial<ReportStyle>) =>
    write<{ ok: boolean; style: ReportStyle }>(
      u("/api/v1/reports/style"), "PUT", { style }),

  createReport: (name: string, description: string, spec: ReportSpec) =>
    write<{ id: number }>(u("/api/v1/reports"), "POST", { name, description, spec }),

  updateReport: (id: number, patch: Partial<{ name: string; description: string; spec: ReportSpec }>) =>
    write<{ ok: boolean }>(u(`/api/v1/reports/${id}`), "PATCH", patch),

  deleteReport: (id: number) =>
    write<{ ok: boolean }>(u(`/api/v1/reports/${id}`), "DELETE"),

  /** The rendered document, as markup, for the editor's preview pane. */
  previewReport: (spec: ReportSpec, name: string) =>
    writeText(u("/api/v1/reports/preview"), { spec, name }),

  reportUrl: (id: number, format: "html" | "pdf") =>
    u(`/api/v1/reports/${id}/render${format === "pdf" ? "?format=pdf" : ""}`),

  audit: (id: number, limit = 300) =>
    req<AuditBody>(u(`/api/v1/benches/${id}/audit?limit=${limit}`)),

  releaseLease: (id: number, force: boolean) =>
    write<{ released: boolean }>(u(`/api/v1/benches/${id}/lease/release`), "POST", {
      force,
    }),

  createBench: (name: string, description: string, project_id?: number) =>
    write<{ bench_id: number }>(u("/api/v1/benches"), "POST", {
      name,
      description,
      ...(project_id ? { project_id } : {}),
    }),

  updateBench: (
    id: number,
    patch: {
      name?: string;
      description?: string;
      project_id?: number;
      notes_md?: string;
      config?: Record<string, string>;
    },
  ) => write<{ ok: boolean }>(u(`/api/v1/benches/${id}`), "PATCH", patch),

  deleteBench: (id: number, confirmName: string) =>
    write<{ ok: boolean; deleted_runs: number }>(
      u(`/api/v1/benches/${id}`),
      "DELETE",
      { confirm_name: confirmName },
    ),

  /** Delete one run: its steps, verdicts, frames and built reports. The name
   *  is required as confirmation - an id is a number in a URL - and a run an
   *  agent is still publishing into is refused with 409 unless `force`. */
  deleteRun: (id: number, confirmName: string, force = false) =>
    write<{ ok: boolean; run: string; deleted: { steps: number; verdicts: number; files: number } }>(
      u(`/api/v1/runs/${id}`),
      "DELETE",
      { confirm_name: confirmName, ...(force ? { force: true } : {}) },
    ),

  rotateToken: (id: number) =>
    write<{ token: string }>(u(`/api/v1/benches/${id}/token`), "POST", {}),

  /** `limit` is what makes a second page possible. It was never passed, so the
   *  list silently showed the server's default of 50 - of 90 - and said
   *  nothing about the other 40. */
  runs: (benchId?: number, limit?: number, projectId?: number) => {
    const q = new URLSearchParams();
    if (benchId) q.set("bench_id", String(benchId));
    if (projectId) q.set("project_id", String(projectId));
    if (limit) q.set("limit", String(limit));
    const s = q.toString();
    return req<{ runs: Run[] }>(u(`/api/v1/runs${s ? `?${s}` : ""}`));
  },

  run: (id: number) =>
    req<{
      run: Run;
      /** The project the run's bench is in (null for a bench in none): where
       *  the scenario browser for this run's cases lives. */
      project_id?: number | null;
      /** Product, build and campaign, for the breadcrumbs. */
      trail?: Trail;
      about?: RunAbout;
      /** What THIS reader may do with the run, computed per request from the
       *  run's bench. The page uses it to not offer a control the server would
       *  refuse; deleting a run needs maintainer. Absent on older responses. */
      role?: Role | null;
      /** The newest step, while the run is unfinished. Null once it ends. */
      live: Step | null;
      /** The harness's own gate, kept apart from the product's numbers. */
      selftest: { ok?: boolean; summary?: string; files?: string };
      /** The run's own output, capped at both ends when long. */
      log: string;
      /** What changed since the previous run on this bench. Null for the first. */
      transitions: Transitions | null;
      /** The same shape against the last run of the SAME variant on a DIFFERENT
       *  build, anywhere in the project. `transitions` answers "since the run
       *  before this one on this rig", which is a different question and stays
       *  beside it. Null when this run or every candidate has no build ref. */
      transitions_build: (Transitions & {
        baseline: { id: number; name: string; bench_id: number;
                    build: string; build_ref: string; variant: string };
      }) | null;
      /** Why the results are not compared with an earlier run (piece 5): the
       *  run recorded no profile or no test data, or its profile last ran on
       *  other test data (named). Null when a fair comparison exists or none
       *  was ever possible (the first run). */
      not_compared?: { why: "no_profile" | "no_test_data" | "test_data_changed";
                       run_id?: number; run?: string } | null;
      /** Which harness and which product data produced this run, and whether
       *  they moved since the run before it. Empty for runs published before
       *  the bench reported digests. */
      provenance: {
        harness?: string;
        product_data?: string;
        product?: string;
        build?: string;
        build_ref?: string;
        build_recorded?: boolean;
        variant?: string;
        variant_label?: string;
        since?: {
          run_id: number;
          run: string;
          harness_changed: boolean;
          product_data_changed: boolean;
          /** Three-valued on purpose: null is "one of the two runs did not say",
           *  which must not look like "no change". */
          build_changed?: boolean | null;
        };
      };
      verdicts: Record<string, number>;
      /** What each scenario SAID when this run ran - name, requires, plan
       *  entry and the block as written, comments and all. Per run on purpose:
       *  a scenario gets edited, and an old run explained with today's text
       *  would be worse than one explained with none. */
      definitions: Record<
        string,
        {
          name?: string;
          requires?: string[];
          verbs?: string[];
          source?: string;
          plan?: {
            group?: string;
            group_name?: string;
            objective?: string;
            preconditions?: string;
            expected_result?: string;
            priority?: string;
            tags?: string[];
          };
        }
      >;
      /** The plan's grouping as the bench understood it on the day. */
      groups: {
        of: Record<string, string>;
        about: Record<string, string>;
        /** What each test IS: its name, and what it should show. */
        plan: Record<string, { name?: string; exp?: string; klass?: string }>;
        /** Which XSNCF issue covers which case, by the issue's own Test ID. */
        tickets: { of?: Record<string, string[]>; base?: string };
      };
      scenarios: ScenarioSummary[];
      /** What the first failed step of each failed case left: its frames and,
          for a picture comparison, the score. Keyed by case. */
      evidence?: Record<string, FailureEvidence>;
      /** Picture comparisons that passed with little to spare, by case. */
      near?: Record<string, Comparison>;
      /** Known problems in force for this run's cases, by case. */
      known?: Record<string, KnownProblem>;
      /** Who asked for the run and why, from its job; null when nobody queued it. */
      requested?: { reason: string; requested_by: string; at: number; campaign_id: number | null } | null;
    }>(u(`/api/v1/runs/${id}`)),

  runPdfs: (id: number) =>
    req<{ pdfs: { level: string; bytes: number }[] }>(
      u(`/api/v1/runs/${id}/pdfs`),
    ),

  runSteps: (id: number, scenario?: string) =>
    req<{ steps: Step[] }>(
      u(`/api/v1/runs/${id}/steps${scenario ? `?scenario=${encodeURIComponent(scenario)}` : ""}`),
    ),

  // --- people, groups and grants -----------------------------------------
  //
  // `users` answers thinly for a non-admin (id, name, display name) and fully
  // for an admin. One call either way: a picker that only an administrator can
  // populate makes every membership change an admin errand.
  users: () => req<{ users: User[]; admin: boolean }>(u("/api/v1/users")),

  user: (id: number) =>
    req<{
      user: User;
      grants: {
        role: Role;
        scope: "project" | "bench";
        scope_id: number;
        scope_name: string;
        /** The group this reaches them through; empty for a direct grant. */
        through: string;
      }[];
    }>(u(`/api/v1/users/${id}`)),

  createUser: (body: {
    name: string;
    password?: string;
    display_name?: string;
    email?: string;
    is_admin?: boolean;
    must_change_password?: boolean;
  }) =>
    /** `password` comes back ONLY when the server generated one, and only
     *  this once: it is stored as a hash. */
    write<{ user_id: number; password?: string }>(u("/api/v1/users"), "POST", body),

  updateUser: (
    id: number,
    patch: {
      name?: string;
      display_name?: string;
      email?: string;
      is_admin?: boolean;
      is_active?: boolean;
    },
  ) => write<{ ok: boolean }>(u(`/api/v1/users/${id}`), "PATCH", patch),

  resetPassword: (id: number, password?: string) =>
    write<{ ok: boolean; password?: string }>(
      u(`/api/v1/users/${id}/password`), "POST", password ? { password } : {},
    ),

  deleteUser: (id: number, confirm_name: string) =>
    write<{ ok: boolean }>(u(`/api/v1/users/${id}`), "DELETE", { confirm_name }),

  groups: () => req<{ groups: Group[] }>(u("/api/v1/groups")),

  group: (id: number) =>
    req<{
      group: Group;
      members: GroupMember[];
      grants: { id: number; role: Role; scope: "project" | "bench";
                scope_id: number; scope_name: string }[];
      may_edit: boolean;
    }>(u(`/api/v1/groups/${id}`)),

  createGroup: (name: string, description: string) =>
    write<{ group_id: number }>(u("/api/v1/groups"), "POST", { name, description }),

  updateGroup: (id: number, patch: { name?: string; description?: string }) =>
    write<{ ok: boolean }>(u(`/api/v1/groups/${id}`), "PATCH", patch),

  deleteGroup: (id: number, confirm_name: string) =>
    write<{ ok: boolean }>(u(`/api/v1/groups/${id}`), "DELETE", { confirm_name }),

  addToGroup: (id: number, user_id: number, manager = false) =>
    write<{ ok: boolean }>(u(`/api/v1/groups/${id}/members`), "POST",
      { user_id, manager }),

  removeFromGroup: (id: number, user_id: number) =>
    write<{ ok: boolean }>(u(`/api/v1/groups/${id}/members/${user_id}`), "DELETE"),

  members: (scope: "project" | "bench", id: number) =>
    req<{ members: Member[]; inherited?: Member[]; roles: Role[] }>(
      u(`/api/v1/${scope}s/${id}/members`),
    ),

  addMember: (
    scope: "project" | "bench",
    id: number,
    subject: { user_id?: number; group_id?: number },
    role: Role,
  ) =>
    write<{ membership_id: number }>(u(`/api/v1/${scope}s/${id}/members`), "POST",
      { ...subject, role }),

  removeMember: (scope: "project" | "bench", id: number, membershipId: number) =>
    write<{ ok: boolean }>(
      u(`/api/v1/${scope}s/${id}/members/${membershipId}`), "DELETE",
    ),
};

/**
 * TWO FORMATTERS, BECAUSE THERE ARE TWO QUESTIONS.
 *
 * There was one - `toLocaleString({dateStyle:"short", timeStyle:"medium"})` -
 * and it answered neither well. A bench that replied a minute ago read "last
 * seen 9/9/26, 6:25:56 AM", which is a date when the reader wanted "a minute
 * ago"; an audit row from August carried seconds nobody has ever needed; and
 * `dateStyle:"short"` is m/d/y under an en-US browser, so 9/9/26 is fine and
 * 8/9/26 is a coin toss for a team in Poland and France reading a server that
 * stores UTC.
 *
 * `ago` is for recency - a heartbeat, a lease, a login, the newest step. `on`
 * is for the archive - when a run started, when an account was created, a row
 * in a log. `stamp` is the exact value, for a `title` on either.
 */

/**
 * THESE FOUR READ THE CATALOGUE, AND THEY ARE NOT HOOKS.
 *
 * They are called during the render of components that already hold a
 * `useTranslation()` - which is what subscribes those components to a language
 * change and re-runs these with the new words. A component that shows a time
 * and translates nothing else would freeze its dates in the old language, so
 * it must take the hook anyway; there is no component in this app that shows a
 * timestamp and no text.
 *
 * The month names come from the catalogue as one space-separated string rather
 * than an array of twelve keys, because twelve keys are twelve chances to
 * misorder them and one string is read left to right by whoever translates it.
 */
function months(): string[] {
  return i18n.t("time.months").split(/\s+/);
}

/** A date that cannot be misread: "9 Sep, 06:25", with the year when it is not
 *  this one. Never m/d/y, never seconds. The ORDER of day and month lives in
 *  the catalogue, so a language that wants the month first - or an ordinal dot
 *  after the day, as Finnish does - gets it without touching this code. */
export function on(ts: number | null | undefined): string {
  if (!ts) return i18n.t("common.dash");
  const d = new Date(ts * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const parts = {
    day: d.getDate(),
    month: months()[d.getMonth()],
    year: d.getFullYear(),
    time: `${hh}:${mm}`,
  };
  return d.getFullYear() === new Date().getFullYear()
    ? i18n.t("time.onDate", parts)
    : i18n.t("time.onDateWithYear", parts);
}

/** A calendar day, for a deadline: "28 Oct 2026". The year always, because a
 *  deadline is read against a calendar, not against today. */
export function onDay(ts: number | null | undefined): string {
  if (!ts) return i18n.t("common.dash");
  const d = new Date(ts * 1000);
  return i18n.t("time.onDay", { day: d.getDate(), month: months()[d.getMonth()], year: d.getFullYear() });
}

/** How long ago, up to a week; a date beyond that, because "43 days ago" is
 *  arithmetic the reader then has to undo. */
export function ago(ts: number | null | undefined): string {
  if (!ts) return i18n.t("common.dash");
  const s = Math.round(Date.now() / 1000 - ts);
  // A bench whose clock is ahead of ours would otherwise read "in -3 min".
  // Wrong is wrong either way, but a timestamp is at least checkable.
  if (s < 0) return on(ts);
  if (s < 45) return i18n.t("time.justNow");
  if (s < 90 * 60) return i18n.t("time.minutesAgo", { count: Math.round(s / 60) });
  if (s < 36 * 3600) return i18n.t("time.hoursAgo", { count: Math.round(s / 3600) });
  if (s < 7 * 86400) return i18n.t("time.daysAgo", { count: Math.round(s / 86400) });
  return on(ts);
}

/** The exact value, for a `title` beside either of the above. */
export function stamp(ts: number | null | undefined): string {
  if (!ts) return "";
  // The chosen language, not the browser's. Someone reading the page in Polish
  // and hovering a date to check it wants the exact value in the same
  // convention as everything around it; `toLocaleString()` with no argument
  // would answer in whatever the OS is set to.
  return new Date(ts * 1000).toLocaleString(i18n.language);
}

export function duration(from: number, to: number | null): string {
  const s = Math.max(0, Math.round((to ?? Date.now() / 1000) - from));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h
    ? i18n.t("time.durationHours", { h, m })
    : m
      ? i18n.t("time.durationMinutes", { m, s: s % 60 })
      : i18n.t("time.durationSeconds", { s });
}
